// pages/ContentDetailPage.jsx with improved image styling
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { useAuth } from '../context/AuthContext';
import { CONSTANTS } from '../constants/Constants';
import Loader from '../components/common/Spinner';

import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const ContentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [showPdf, setShowPdf] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchContentDetails();
  }, [id]);
  
  const fetchContentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch content details
      const response = await axios.get(`${CONSTANTS.APIURL}/api/smart-contents/${id}`);
      setContent(response.data.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching content details:', error);
      toast.error('Failed to load content details');
      setLoading(false);
      navigate('/smart-contents');
    }
  };
  
  const handleViewPdf = async () => {
    if (!currentUser) {
      toast.warning('Please login to view the PDF');
      navigate('/login', { state: { from: `/content/${id}` } });
      return;
    }
    
    try {
      setLoading(true);
      
      // Get signed URL for PDF
      const response = await axios.get(`${CONSTANTS.APIURL}/api/smart-contents/${id}/pdf`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setPdfUrl(response.data.data.pdfUrl);
      setShowPdf(true);
      setLoading(false);
    } catch (error) {
      console.error('Error getting PDF URL:', error);
      
      // Handle different error cases
      if (error.response && error.response.status === 401) {
        toast.error('Your session has expired. Please login again.');
        navigate('/login', { state: { from: `/content/${id}` } });
      } else if (error.response && error.response.status === 403) {
        toast.error('You need to upgrade your plan to access this content');
      } else {
        toast.error('Failed to load PDF');
      }
      
      setLoading(false);
    }
  };
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };
  
  const goToPrevPage = () => {
    setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
  };
  
  const goToNextPage = () => {
    setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages));
  };
  
  // Helper function to format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Determine category name from category ID
  const getCategoryName = (categoryId) => {
    const categories = {
      'notes': 'Study Notes',
      'mindmaps': 'Mind Maps',
      'summaries': 'Topic Summaries',
      'current': 'Current Affairs',
      'infographics': 'Infographics'
    };
    
    return categories[categoryId] || 'Study Material';
  };
  
  if (loading) {
    return <Loader />;
  }
  
  if (!content) {
    return (
      <div className="pt-24 pb-16 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Content Not Found</h1>
        <p className="text-gray-600 mb-6">The content you're looking for does not exist or has been removed.</p>
        <Link 
          to="/smart-contents"
          className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Back to All Content
        </Link>
      </div>
    );
  }
  
  return (
    <div className="pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex py-4 text-sm mt-8">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/smart-content" className="text-gray-500 hover:text-gray-700">Smart Contents</Link>
            </li>
            <li className="flex items-center">
              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <span className="text-gray-700">{content.title}</span>
            </li>
          </ol>
        </nav>
        
        {/* Back button for mobile */}
        <div className="md:hidden mb-6">
          <Link 
            to="/smart-contents"
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </Link>
        </div>
        
        {/* Content Detail */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Content Header - UPDATED IMAGE CONTAINER */}
          <div className="relative">
            {/* Updated height for better appearance */}
            <div className="h-48 sm:h-64 md:h-80 relative">
              <img 
                src={content.imageUrl} 
                alt={content.title}
                className="object-cover w-full h-full"
              />
              {/* Overlay with gradient for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
            </div>
            
            {/* Position text at the bottom of the image */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <div className="flex items-center mb-2">
                <span className="px-2 py-1 bg-gray-700 rounded-md text-xs font-medium text-white">
                  {getCategoryName(content.category)}
                </span>
                {content.popular && (
                  <span className="ml-2 px-2 py-1 bg-yellow-600 rounded-md text-xs font-medium text-white">
                    Popular
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">{content.title}</h1>
            </div>
          </div>
          
          {/* Content Body */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-500">
              <div className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{content.pages} pages</span>
              </div>
              <div className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>{content.format}</span>
              </div>
              <div className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Last Updated: {formatDate(content.updatedAt)}</span>
              </div>
            </div>
            
            <div className="prose max-w-none mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Description</h2>
              <p className="text-gray-700 mb-6">{content.description}</p>
              
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-3">What You'll Learn</h2>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <svg className="mt-1 mr-2 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Comprehensive understanding of key concepts covered in the material</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="mt-1 mr-2 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Critical insights that are frequently asked in UPSC examinations</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="mt-1 mr-2 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Expert analysis and simplified explanations of complex topics</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* View PDF Button */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <button
                onClick={handleViewPdf}
                disabled={loading}
                className="w-full sm:w-auto px-6 py-3 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex justify-center items-center"
              >
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View PDF
                  </>
                )}
              </button>
              
              <Link
                to="/smart-contents"
                className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 transition-colors text-center"
              >
                Back to All Content
              </Link>
            </div>
          </div>
        </div>
        
        {/* PDF Viewer Modal */}
        {showPdf && pdfUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 truncate">{content.title}</h3>
                <button
                  onClick={() => setShowPdf(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex-1 overflow-auto">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="flex items-center justify-center h-full py-20">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
                    </div>
                  }
                  error={
                    <div className="flex flex-col items-center justify-center h-full py-20">
                      <svg className="h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-red-600 text-center">
                        Failed to load PDF. Please try again later.
                      </p>
                    </div>
                  }
                  className="pdf-container"
                >
                  <Page 
                    pageNumber={pageNumber} 
                    scale={1.2}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              </div>
              
              <div className="p-4 border-t flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Page {pageNumber} of {numPages || '?'}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                    className="px-3 py-1.5 border border-gray-600 rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Related Content */}
        {/* This would be implemented with a query to fetch related content from the same category */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Content</h2>
          <p className="text-gray-600">Coming soon...</p>
        </div>
      </div>
    </div>
  );
};

export default ContentDetailPage;